import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setAuthToken, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Zap, ArrowRight, CheckCircle2, ShieldCheck, CreditCard, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Signup() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ company_name: "", kvk: "", name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Ploegbord — 7 dagen gratis proberen"; }, []);
  useEffect(() => {
    if (user && user.id && !loading) nav("/planbord", { replace: true });
  }, [user, nav, loading]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Enige actie: register-company → account + 7-daagse trial, ZONDER Stripe-scherm.
      const reg = await api.post("/auth/register-company", form);
      if (reg.data?.access_token) setAuthToken(reg.data.access_token, true);
      await refresh();
      toast.success("Welkom! Je hebt 7 dagen gratis toegang.");
      nav("/planbord", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Registratie mislukt";
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="signup-page">
      <header className="border-b-4 border-orange-600 bg-slate-900 shadow-[0_4px_0_rgba(0,0,0,0.15)]">
        <div className="max-w-6xl mx-auto px-4 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="/logo-ploegbord.webp?v=8" alt="Ploegbord" className="h-12 md:h-14 w-auto"/>
          </Link>
          <Link to="/login" className="text-sm font-black uppercase tracking-widest text-white hover:text-orange-400">Al een account? Inloggen →</Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-12 grid lg:grid-cols-[1.1fr_0.9fr] gap-10">
        <div>
          <div className="text-orange-600 text-xs font-black uppercase tracking-widest">Start je bedrijf</div>
          <h1 className="pb-heading text-3xl md:text-5xl font-black mt-1">7 dagen gratis proberen</h1>
          <p className="mt-3 text-slate-700 font-semibold">
            Geen creditcard, geen iDEAL, geen betaalgegevens nodig. Direct toegang tot het planbord.
            Na 7 dagen kies je pas een pakket en betaal je via iDEAL.
          </p>

          <form onSubmit={submit} className="mt-6 pb-card p-6 space-y-4">
            <Field label="Bedrijfsnaam" required>
              <input className="pb-input" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Van Dijk Bouw" data-testid="signup-company"/>
            </Field>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="KVK (optioneel)">
                <input className="pb-input" value={form.kvk} onChange={(e) => setForm({ ...form, kvk: e.target.value })} placeholder="12345678" data-testid="signup-kvk"/>
              </Field>
              <Field label="Jouw naam" required>
                <input className="pb-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name"/>
              </Field>
            </div>
            <Field label="E-mailadres" required>
              <input type="email" className="pb-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email"/>
            </Field>
            <Field label="Wachtwoord" required>
              <input type="password" className="pb-input" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 8 tekens" data-testid="signup-password"/>
            </Field>

            <button type="submit" disabled={loading} className="pb-btn-primary w-full flex items-center justify-center gap-2 !min-h-[56px] text-base disabled:opacity-60" data-testid="signup-submit">
              {loading ? "Account aanmaken…" : <><Zap size={20}/> Start 7 dagen gratis <ArrowRight size={18}/></>}
            </button>
            <div className="text-xs text-center text-slate-500 font-bold">
              Door door te gaan ga je akkoord met onze <Link to="/voorwaarden" className="underline">voorwaarden</Link>.
              Geen automatische afboeking · maandelijks opzegbaar.
            </div>
          </form>
          <div className="pt-4 border-t border-slate-200 mt-6 text-center text-[11px] font-bold text-slate-500 space-x-3" data-testid="signup-legal-footer">
            <Link to="/privacy" className="hover:text-orange-600 underline decoration-dotted">Privacyverklaring</Link>
            <span className="text-slate-300">·</span>
            <Link to="/voorwaarden" className="hover:text-orange-600 underline decoration-dotted">Algemene voorwaarden</Link>
            <span className="text-slate-300">·</span>
            <a href="mailto:info@ploegbord.nl" className="hover:text-orange-600 underline decoration-dotted">info@ploegbord.nl</a>
          </div>
        </div>

        <aside>
          <div className="pb-card p-6 sticky top-6">
            <div className="text-xs font-black uppercase tracking-widest text-slate-500">Zo werkt het</div>
            <ol className="mt-3 space-y-4">
              <Step icon={Zap} title="Account aanmaken" body="Vul je gegevens in — je bent direct binnen. Geen betaalscherm." />
              <Step icon={Clock} title="7 dagen gratis testen" body="Volledige toegang tot planbord, urenregistratie en exports. Geen data-limieten." />
              <Step icon={CreditCard} title="Kies pakket + iDEAL" body="Na 7 dagen kies je een pakket en betaal je met iDEAL (of Wero). Automatische incasso volgt daarna." />
            </ol>
            <div className="mt-6 border-t-2 border-slate-200 pt-4">
              <div className="text-xs font-black uppercase tracking-widest text-slate-500">Wat krijg je?</div>
              <ul className="mt-3 text-sm space-y-2 font-semibold">
                {[
                  "Drag-and-drop planbord (week + dag)",
                  "Urenregistratie met pauzes + auto-stop",
                  "Excel + PDF exports (loonstrook-ready)",
                  "PWA — werkt offline op mobiel",
                  "Data blijft van jou — altijd exporteerbaar",
                ].map((t) => (
                  <li key={t} className="flex gap-2 items-start">
                    <CheckCircle2 size={16} className="text-orange-600 shrink-0 mt-0.5"/> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-4 rounded-lg bg-emerald-50 border-2 border-emerald-500 p-3 flex items-start gap-2">
              <ShieldCheck size={18} className="text-emerald-700 shrink-0 mt-0.5" />
              <div className="text-xs text-emerald-900 font-bold">
                <span className="font-black">100% risicoloos.</span> We vragen geen betaalgegevens tijdens de proef. Loopt je week soepel? Betaal je verder. Zo niet, doe je niets.
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Step({ icon: Icon, title, body }) {
  return (
    <li className="flex gap-3">
      <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <div className="pb-heading font-black">{title}</div>
        <div className="text-xs text-slate-600 font-semibold mt-0.5">{body}</div>
      </div>
    </li>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-black uppercase tracking-widest text-slate-700">{label}{required && <span className="text-orange-600"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
